/**
 * Which default scene is authoritative for a route: the override cascade, or
 * the product's own products.js scene?
 *
 * Normally the cascade wins. The exception is KED products (Kotlin, Ktor, KMP,
 * Compose Multiplatform, Exposed): they have no per-set/per-layout admin
 * override rows, so the wildcard `*` rows written for IDE releases would leak
 * IDE-flavored content — and IDE crops — onto KED banners. When no row targets
 * the product AND a products.js scene resolves, that scene is the source of
 * truth.
 *
 * This lives here because TWO consumers make the choice and must not disagree:
 * `useInitialScene` (scene content) and `useEffectiveBgTransform` (the derived
 * bg crop). They did disagree — the transform selector always read the cascade,
 * so every KED release banner rendered at the IDE crop with the rotation
 * dropped while its texts came from the right scene (#567).
 */

import {KED_PRODUCTS_SET} from "data/products/index";

interface OverrideLayer {
  product?: string | null;
}

interface PreferInput {
  /** Product code for the current route. */
  product?: string | null;
  /** Override rows that matched the route's cascade (v3 `layers`, or the client-resolved list). */
  layers?: OverrideLayer[] | null;
  /**
   * Does a products.js scene resolve for this route? Flag-off that's a
   * `legacySceneId`; flag-on the resolved response's `product_default_hash`
   * says the server resolved the same id with the same shared function.
   */
  hasProductDefaultScene?: boolean;
}

export function prefersProductDefaultScene({
  product,
  layers,
  hasProductDefaultScene,
}: PreferInput): boolean {
  if (!product || !KED_PRODUCTS_SET.has(product)) return false;
  if (!hasProductDefaultScene) return false;
  // A row that targets the product specifically is a deliberate admin decision
  // and outranks the products.js scene; wildcard rows are what this guards.
  return !(layers ?? []).some((layer) => layer?.product === product);
}

interface ResolvedResponse {
  scene_data?: unknown;
  product_default_scene?: unknown;
  product_default_hash?: string | null;
  layers?: OverrideLayer[] | null;
}

/** Did the override cascade resolve a scene at all? Mirrors useInitialScene. */
function hasCascadeScene(resolved: ResolvedResponse): boolean {
  const scene = resolved.scene_data;
  if (!scene || typeof scene !== "object") return false;
  return Object.keys(scene as Record<string, unknown>).length > 0;
}

/**
 * Is the products.js scene — not the cascade — the one the route renders?
 *
 * The two are EXCLUSIVE, exactly as useInitialScene picks them
 * (`useOverridePath ? overrideSceneData : legacySceneData`): an empty cascade
 * means the products.js scene is the route's default. The KED exception makes it
 * win even over a cascade that did resolve.
 */
export function isProductDefaultAuthoritative(
  resolved: ResolvedResponse | null | undefined,
  product?: string | null
): boolean {
  if (!resolved?.product_default_hash) return false;
  const preferProductDefault = prefersProductDefaultScene({
    product,
    layers: resolved.layers,
    hasProductDefaultScene: true,
  });
  return preferProductDefault || !hasCascadeScene(resolved);
}

/**
 * The scene the derived-transform layers must read, given a v3
 * `/default-scenes/resolved` response. Returns null when neither half resolved.
 */
export function authoritativeSceneData(
  resolved: ResolvedResponse | null | undefined,
  product?: string | null
): unknown {
  if (!resolved) return null;
  if (isProductDefaultAuthoritative(resolved, product)) {
    return resolved.product_default_scene ?? null;
  }
  return resolved.scene_data ?? null;
}

/**
 * That scene as a transform layer for the variation a banner DISPLAYS.
 *
 * The cascade needs no gate — the resolve query is keyed by background, so what
 * comes back already belongs to the displayed variation. A products.js scene
 * does: it pins one `bgOption` and its crops were authored against that
 * variation's artwork. Legacy applied them only while the displayed variation was
 * the scene's own (useBackgrounds' `shouldPreferInitialSceneTransforms`) and let
 * the gallery artwork's own crop — now the catalog's — win otherwise.
 */
export function sceneTransformLayerForOption(
  resolved: ResolvedResponse | null | undefined,
  product: string | null | undefined,
  optionKey: string
): unknown {
  const scene = authoritativeSceneData(resolved, product);
  if (!scene) return null;
  if (!isProductDefaultAuthoritative(resolved, product)) return scene;
  const pinned = String((scene as {bgOption?: unknown}).bgOption ?? "1");
  return pinned === String(optionKey) ? scene : null;
}
